import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Earnings.css';
import { coursesByInstructor, getInstructorEarningsByCourse, INSTRUCTOR_URL } from '../../Services/Services';
import { toast } from 'react-toastify';
import axios from 'axios';
import ClipLoader from 'react-spinners/ClipLoader';

const Earnings = ({ setActiveSectionFunc }) => {
  const { t } = useTranslation();
  const [earnings, setEarnings] = useState(0);
  const [enrollments, setEnrollments] = useState(0);
  const [showWithdrawalLoading, setShowWithdrawalLoading] = useState(false);
  const userId = localStorage.getItem('userId');
  const [hasPaymentAccount, setHasPaymentAccount] = useState(false);
  const [totalWithdrawnAmount, setTotalWithdrawnAmount] = useState(0);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const fetchWithdrawalHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${INSTRUCTOR_URL}/${userId}/withdrawal-history`);
      setWithdrawalHistory(response?.data ?? []);
    } catch (error) {
      toast.dismiss();
      console.error('Error fetching withdrawal history:', error);
      toast.error('Error fetching withdrawal history.');
    }
  }, [userId]);
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await coursesByInstructor(userId);
        const fetchedCourses = Array.isArray(response?.data) ? response?.data : [];

        let totalEarnings = 0;
        let totalEnrollments = 0;

        const approvedPercentageCourses = fetchedCourses.filter(
          (course) => course.approvalStatus === 'APPROVED' && course.paymentType === 'PERCENTAGE'
        );

        for (let course of approvedPercentageCourses) {
          const earningsResponse = await getInstructorEarningsByCourse(course?.courseId);
          const earningsData = earningsResponse?.data;

          totalEarnings += earningsData?.earnings ?? 0;
          totalEnrollments += earningsData?.enrollmentCount ?? 0;
        }

        setEarnings(totalEarnings);
        setEnrollments(totalEnrollments);

      } catch (error) {
        console.error('Error fetching courses or earnings:', error);
      }
    };

    const checkPaymentAccount = async () => {
      try {
        const response = await axios.get(`${INSTRUCTOR_URL}/${userId}/has-payment-account`);
        setHasPaymentAccount(response?.data?.hasPaymentAccount ?? false);
      } catch (error) {
        console.error('Error checking payment account:', error);
      }
    };



    const handleDollarClick = async () => {
      try {
        const withdrawnResponse = await axios.get(`${INSTRUCTOR_URL}/${userId}/total-withdrawal-earnings`);
        const withdrawnAmount = withdrawnResponse?.data ?? 0;
        setTotalWithdrawnAmount(withdrawnAmount);
      } catch (error) {
        toast.dismiss();
        if (error.response && error.response.status === 500) {
          toast.error(error.response.data || 'Service Unavailable: Please try again later.');
        } else {
          console.error('Error fetching earnings:', error);
        }
      }
    };

    fetchCourses();
    handleDollarClick();
    checkPaymentAccount();
    fetchWithdrawalHistory();
  }, [userId, fetchWithdrawalHistory]);

  const navigateToPaymentInfo = () => {
    setActiveSectionFunc('PaymentInfo');
  };

  const handleWithdrawClick = async () => {
    if (!hasPaymentAccount) {
      toast.error('Please add payment information to proceed with the withdrawal.');
      return;
    }
    toast.dismiss();

    try {
      setShowWithdrawalLoading(true);
      const response = await axios.post(`${INSTRUCTOR_URL}/${userId}/request-withdrawal`);
      toast.success(response?.data?.message || 'Withdrawal request successful!');
      fetchWithdrawalHistory();

    } catch (error) {
      toast.dismiss();
      if (error.response && error.response.status === 500) {
        toast.error('Service Unavailable: Please try again later.');
      } else if (error.response && error.response.status === 400) {
        toast.error(error.response?.data || 'Bad request: Please check the input or data.');
      } else {
        toast.error('An error occurred while processing withdrawal. Please try again later.');
      }
    } finally {
      setShowWithdrawalLoading(false);
    }
  };

  return (
    <div className="earnings-container">
      <div className="earnings-report">
        <h2 className="report-title">{t('YourEarningsReport')}</h2>
        <p><strong>{t('TotalEarnings')}:</strong> ₹{earnings.toFixed(2)}</p>
        <p><strong>Total Amount Withdrawn:</strong> ₹{totalWithdrawnAmount}</p>
        <p><strong>Amount Available for Withdrawal:</strong> ₹{earnings - totalWithdrawnAmount}</p>
        <p><strong>{t('CoursesSold')}:</strong> {enrollments}</p>

        <button onClick={handleWithdrawClick} title="Withdraw" className="withdrawal-modal-button" disabled={showWithdrawalLoading}>
          {!showWithdrawalLoading ? 'Withdraw' : <ClipLoader color="#fff" loading={showWithdrawalLoading} size={10} />}
        </button>
        {!hasPaymentAccount && (
          <div>
            <p style={{ color: 'red', cursor: 'pointer' }} onClick={navigateToPaymentInfo}>
              Add Payment Info Before Withdraw
            </p>
          </div>
        )}
      </div>

      <div className="withdrawal-history">
        <h3>{t('WithdrawalHistory')}</h3>

        <table className="course-table">
          <thead>
            <tr>
              <th>{t('Requested Amount')}</th>
              <th>{t('Status')}</th>
              <th>{t('Requested On')}</th>
              <th>{t('Completion Date:')}</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(withdrawalHistory) && withdrawalHistory.length > 0 ? withdrawalHistory.map((withdrawal, index) => (
              <tr key={index}>
                <td>₹{withdrawal?.requestedAmount}</td>
                <td>{withdrawal?.status}</td>
                <td>{new Date(withdrawal?.requestDate).toLocaleDateString()}</td>
                <td>{withdrawal?.completionDate ? new Date(withdrawal?.completionDate).toLocaleDateString() : 'Pending'}</td>
              </tr>
            )) : (
              <tr><td colSpan="4">No withdrawal history available</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Earnings;
